<template>
  <div class="report-orders">
    <mainmenu selected="report-orders"></mainmenu>
    <div class="container-fluid report-orders-shell">
      <div class="report-orders-header">
        <div>
          <h1>{{$t('report_order.page_title')}}</h1>
          <div class="report-orders-summary">
            {{$t('report_order.total_orders')}} <strong>{{PaginationInfo.RowCount}}</strong>
          </div>
        </div>
        <a :href="$http.options.root + 'report_order/export_excel'" target="_blank" class="el-button el-button--success report-export-button">
          <i class="el-icon-download"></i>
          {{$t('report_order.excel')}}
        </a>
      </div>

      <div class="report-filter-card">
        <el-form :model="Filters" label-position="top">
          <div class="report-filter-grid">
            <div class="filter-label">{{$t('report_order.filters')}}</div>

            <el-form-item :label="$t('report_order.article_code')">
              <el-autocomplete
                class="full-width"
                v-model="Filters.ArticleCode"
                :fetch-suggestions="queryCodes"
                :placeholder="$t('report_order.search_article')">
              </el-autocomplete>
            </el-form-item>

            <el-form-item :label="$t('report_order.dates')">
              <el-date-picker
                class="full-width"
                v-model="Filters.Dates"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                type="daterange"
                align="right"
                unlink-panels
                range-separator="To"
                :start-placeholder="$t('report_order.start_date')"
                :end-placeholder="$t('report_order.end_date')"
                :picker-options="pickerOptions">
              </el-date-picker>
            </el-form-item>

            <el-form-item :label="$t('report_order.category')">
              <el-select v-model="Filters.IdCategory" class="full-width" filterable>
                <el-option :label="$t('report_order.all')" :value="-1" :key="-1"></el-option>
                <el-option v-for="c in Categories" :label="c.Name" :value="c.Id" :key="c.Id"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item :label="$t('report_order.order_status')">
              <el-select v-model="Filters.Status" class="full-width">
                <el-option :label="$t('report_order.all')" value="all"></el-option>
                <el-option :label="$t('report_order.accepted')" value="accepted"></el-option>
                <el-option :label="$t('report_order.rejected')" value="rejected"></el-option>
                <el-option :label="$t('report_order.pending')" value="pending"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item :label="$t('report_order.buyer')">
              <el-select v-model="Filters.IdBuyer" class="full-width" filterable>
                <el-option :label="$t('report_order.all')" :value="-1" :key="-1"></el-option>
                <el-option v-for="u in AllUsers" :label="u.Name" :value="u.Id" :key="u.Id"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item :label="$t('report_order.seller')">
              <el-select v-model="Filters.IdSeller" class="full-width" filterable>
                <el-option :label="$t('report_order.all')" :value="-1" :key="-1"></el-option>
                <el-option v-for="u in AllUsers" :label="u.Name" :value="u.Id" :key="u.Id"></el-option>
              </el-select>
            </el-form-item>

            <div class="filter-actions">
              <el-button type="primary" icon="el-icon-search" @click="applyFilters">{{$t('report_order.filters')}}</el-button>
              <el-button icon="el-icon-refresh" @click="resetFilters">{{$t('report_order.reset')}}</el-button>
            </div>
          </div>
        </el-form>
      </div>

      <div class="report-orders-card">
        <div class="report-card-title">
          <div>
            <span>{{$t('report_order.orders')}}</span>
            <small>{{$t('report_order.sorted_by_id')}}</small>
          </div>
          <span class="total-pill">{{$t('report_order.total')}} {{PaginationInfo.RowCount}}</span>
        </div>

        <el-table
          class="report-orders-table"
          :data="Results"
          stripe
          v-loading="tableLoadingVisible"
          :empty-text="$t('report_order.empty_results')">
          <el-table-column prop="Id" :label="$t('report_order.unique_id')" width="70"></el-table-column>

          <el-table-column :label="$t('report_order.request_date')" width="150">
            <template slot-scope="scope">{{formatDate(scope.row.RequestDate)}}</template>
          </el-table-column>

          <el-table-column prop="SellerFactoryCode" :label="$t('report_order.factory_code')" width="115"></el-table-column>
          <el-table-column prop="SellerName" :label="$t('report_order.seller_name')" width="185" show-overflow-tooltip></el-table-column>

          <el-table-column prop="BuyerFactoryCode" :label="$t('report_order.buyer_factory_code')" width="135"></el-table-column>
          <el-table-column prop="BuyerName" :label="$t('report_order.buyer_name')" width="185" show-overflow-tooltip></el-table-column>

          <el-table-column :label="$t('report_order.article_info')" min-width="360">
            <template slot-scope="scope">
              <div class="article-main"><strong>{{scope.row.ArticleNumber}}</strong> - {{scope.row.ProductName}}</div>
              <div class="article-category">{{scope.row.ProductCategory}}</div>
              <p class="article-description">{{scope.row.ProductDescription}}</p>
            </template>
          </el-table-column>

          <el-table-column :label="$t('report_order.order_status')" width="170">
            <template slot-scope="scope">
              <span :class="['status-pill', statusClass(scope.row.RequestStatus)]">{{statusLabel(scope.row.RequestStatus)}}</span>
              <el-tooltip v-if="scope.row.RequestStatus=='rejected' && scope.row.RejectReason" :content="scope.row.RejectReason" placement="top">
                <div class="reject-reason">{{scope.row.RejectReason}}</div>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column prop="Quantity" :label="$t('report_order.quantity')" width="110" align="right"></el-table-column>
          <el-table-column :label="$t('report_order.price')" width="105" align="right">
            <template slot-scope="scope">{{scope.row.Price|format_money}} &euro;</template>
          </el-table-column>

          <el-table-column fixed="right" :label="$t('report_order.operations')" width="150">
            <template slot-scope="scope">
              <a
                v-if="scope.row.RequestStatus=='accepted'"
                :href="scope.row.PdfDownloadUrl"
                target="_blank"
                class="delivery-link">
                <i class="el-icon-download"></i>
                {{$t('report_order.download_delivery_note')}}
              </a>
              <span v-else class="no-action">{{$t('report_order.no_action')}}</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="report-pagination">
          <el-pagination
            @size-change="refresh_info"
            @current-change="refresh_info"
            :page-size.sync="PaginationInfo.PerPage"
            :current-page.sync="PaginationInfo.Page"
            :total="PaginationInfo.RowCount"
            layout="total, prev, pager, next">
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from "./../../widgets/MainMenu";
import settings from "./../../backend/LocalSettings";
import moment from 'moment';

function buildDefaultFilters() {
  return {
    Dates:[ moment().startOf("year").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
    ArticleCode: "",
    IdCategory: -1,
    Status: "all",
    IdBuyer: -1,
    IdSeller: -1
  };
}

export default {
  name: "report-orders",
  components: {
    "mainmenu": MainMenu
  },
  data () {
    return {
      tableLoadingVisible: false,
      Results: [],
      Filters: buildDefaultFilters(),
      PaginationInfo: { Page: 1, PerPage: 10, RowCount: 0, PageSizes: [10, 25, 50, 100] },
      Categories: [],
      AllUsers: [],
      pickerOptions: {
        shortcuts: [{
          text: 'Last week',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Last month',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Last 3 months',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
            picker.$emit('pick', [start, end]);
          }
        }]
      }
    }
  },
  methods: {
    formatDate: function(value) {
      if(!value || value === "0000-00-00 00:00:00") return "-";
      var parts = String(value).split(/[- :]/);
      if(parts.length < 5) return value;
      var dateValue = new Date(Date.UTC(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
        Number(parts[3] || 0),
        Number(parts[4] || 0),
        Number(parts[5] || 0)
      ));
      if(isNaN(dateValue.getTime())) return value;
      var pad = function(part) { return String(part).padStart(2, "0"); };
      return pad(dateValue.getDate()) + "." + pad(dateValue.getMonth() + 1) + "." + dateValue.getFullYear() + " " + pad(dateValue.getHours()) + ":" + pad(dateValue.getMinutes());
    },
    statusLabel: function(status) {
      if(status === "accepted") return this.$t('report_order.accepted');
      if(status === "pending") return this.$t('report_order.pending');
      if(status === "rejected") return this.$t('report_order.rejected');
      return status || "-";
    },
    statusClass: function(status) {
      if(status === "accepted") return "status-accepted";
      if(status === "pending") return "status-pending";
      if(status === "rejected") return "status-rejected";
      return "status-neutral";
    },
    resetFilters: function(){
      this.Filters = buildDefaultFilters();
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    applyFilters: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    get_info: function() {
      this.tableLoadingVisible = true;
      this.$http
        .post('report_order/get_info',  {} )
        .then(function (response) {
          this.tableLoadingVisible = false;
          if( settings.verify_response(response, this.$rights)) {
            this.Categories = response.body.Categories;
            this.AllUsers = response.body.AllUsers;
            this.refresh_info();
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.tableLoadingVisible = false;
          this.$message.error("Could not load orders report.");
        });
    },
    refresh_info: function() {
      this.tableLoadingVisible = true;
      this.$http
        .post('report_order/refresh_info',  { Filters: this.Filters, PaginationInfo: this.PaginationInfo } )
        .then(function (response) {
          this.tableLoadingVisible = false;
          if( settings.verify_response(response, this.$rights)) {
            this.Results = response.body.Results;
            this.PaginationInfo = response.body.PaginationInfo;
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.tableLoadingVisible = false;
          this.$message.error("Could not load orders report data.");
        });
    },
    queryCodes: function(queryString, cb){
      this.$http
        .post('products/search_code',  { query: queryString } )
        .then(function (response) {
          if( settings.verify_response(response, this.$rights)) {
            cb(response.body.Results);
          } else {
            this.$router.push("/");
          }
        });
    },
  },
  mounted: function() {
    this.get_info();
  }
};
</script>

<style lang="less" scoped>
.report-orders-shell {
  padding: 14px 10px 28px;
}

.report-orders-header,
.report-filter-card,
.report-orders-card {
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 26px rgba(25, 80, 99, 0.13);
}

.report-orders-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
  padding: 18px;

  h1 {
    margin: 0;
    color: #152b38;
    font-size: 31px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 36px;
  }
}

.report-orders-summary {
  margin-top: 2px;
  color: rgba(21, 43, 56, 0.72);
  font-size: 13px;
  font-weight: 600;
}

.report-export-button {
  flex: 0 0 auto;
}

.report-filter-card {
  margin-bottom: 14px;
  padding: 12px 18px 14px;
}

.report-filter-grid {
  display: grid;
  align-items: end;
  grid-template-columns: 56px minmax(190px, 1.1fr) minmax(270px, 1.4fr) minmax(180px, 1fr) minmax(170px, 0.8fr) minmax(190px, 1fr) minmax(190px, 1fr) auto;
  gap: 10px;
}

.filter-label {
  align-self: center;
  color: #314754;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.report-filter-card /deep/ .el-form-item {
  margin-bottom: 0;
}

.report-filter-card /deep/ .el-form-item__label {
  padding-bottom: 5px;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 16px;
}

.report-filter-card /deep/ .el-input__inner {
  height: 34px;
  line-height: 34px;
}

.report-filter-card /deep/ .el-date-editor .el-range-separator {
  line-height: 26px;
}

.full-width {
  width: 100%;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 1px;
  white-space: nowrap;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.report-orders-card {
  overflow: hidden;
}

.report-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 18px;
  border-bottom: 1px solid #e6edf2;
  background: #fbfdfe;
  color: #223947;
  font-size: 15px;
  font-weight: 800;

  small {
    display: block;
    margin-top: 2px;
    color: #6b7c87;
    font-size: 12px;
    font-weight: 700;
  }
}

.total-pill {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  background: #eef6f9;
  color: #2b6275;
  font-size: 12px;
  font-weight: 800;
}

.report-orders-table /deep/ th {
  padding: 10px 0;
  background: #f4f8fa;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
}

.report-orders-table /deep/ td {
  padding: 8px 0;
  border-bottom-color: #e7edf2;
  color: #405461;
}

.article-main {
  color: #314754;
  line-height: 19px;
}

.article-category {
  margin-top: 2px;
  color: #526976;
  font-style: italic;
  line-height: 18px;
}

.article-description {
  display: -webkit-box;
  margin: 4px 0 0;
  overflow: hidden;
  color: #64727c;
  font-size: 12px;
  line-height: 17px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-width: 72px;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
}

.status-accepted {
  background: #eaf6e5;
  color: #3d9b2f;
}

.status-pending {
  background: #fff4e5;
  color: #d5891d;
}

.status-rejected {
  background: #fdeaea;
  color: #c63e3e;
}

.status-neutral {
  background: #eef4f8;
  color: #426274;
}

.reject-reason {
  margin-top: 5px;
  overflow: hidden;
  color: #9b5555;
  font-size: 11px;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delivery-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 112px;
  padding: 6px 10px;
  border: 1px solid #69bd48;
  border-radius: 4px;
  background: #f2fbef;
  color: #45a72e;
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  text-decoration: none;

  i {
    margin-right: 4px;
  }
}

.no-action {
  color: #9aa8af;
  font-size: 12px;
}

.report-pagination {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px;
  border-top: 1px solid #e6edf2;
  background: #fbfdfe;
}

@media (max-width: 1400px) {
  .report-filter-grid {
    grid-template-columns: 56px repeat(3, minmax(180px, 1fr));
  }

  .filter-actions {
    grid-column: 2 / -1;
  }
}

@media (max-width: 760px) {
  .report-orders-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .report-filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-label,
  .filter-actions {
    grid-column: auto;
  }
}
</style>
